import { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { collection, addDoc, doc, setDoc } from 'firebase/firestore';
import { auth, db, functions } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';

// Check if we're in demo mode
const isDemo = process.env.REACT_APP_IS_DEMO === 'true';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isDemo) {
        // DEMO MODE: Create account with immediate access, no payment required
        console.log('Creating demo account with immediate access...');

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const userId = userCredential.user.uid;
        console.log('Demo user created with UID:', userId);

        // Create company for demo use
        const companyRef = await addDoc(collection(db, 'companies'), {
          name: companyName.trim(),
          adminUserId: userId,
          members: [userId],
          isDemoAccount: true,
          createdAt: new Date().toISOString()
        });
        const companyId = companyRef.id;

        // Create user document
        await setDoc(doc(db, 'users', userId), {
          email: email,
          name: name,
          companyId: companyId,
          isDemoAccount: true,
          createdAt: new Date().toISOString()
        });

        console.log('Demo account created successfully! Redirecting to dashboard...');
        navigate('/dashboard');

      } else {
        // PRODUCTION MODE: Create account and initiate PayPal subscription
        console.log('Creating admin account and initiating PayPal subscription for first license...');

        const adminCredential = await createUserWithEmailAndPassword(auth, email, password);
        const adminId = adminCredential.user.uid;
        console.log('Admin created with UID:', adminId);

        // Create company with one license that will be assigned to admin
        const companyRef = await addDoc(collection(db, 'companies'), {
          name: companyName.trim(),
          adminUserId: adminId,
          members: [adminId],
          licenses: [] // Will be populated by PayPal webhook after payment
        });
        const companyId = companyRef.id;

        // Create user document
        await setDoc(doc(db, 'users', adminId), {
          email: email,
          name: name,
          companyId: companyId
        });

        // Create PayPal subscription for the admin's first license
        console.log('Creating PayPal subscription for admin license...');
        const createSubscription = httpsCallable(functions, 'createPayPalSubscription');
        const result = await createSubscription({
          companyId: companyId,
          licenseCount: 1, // Admin gets first license
          adminEmail: email,
          adminName: name
        });

        if (result.data.success && result.data.approvalUrl) {
          console.log('PayPal subscription created, redirecting to approval...');
          // Redirect to PayPal for the required payment
          window.location.href = result.data.approvalUrl;
        } else {
          throw new Error(result.data.error || 'Failed to create PayPal subscription');
        }
      }

    } catch (err) {
      console.error('Signup error:', err.message);
      setError(`Failed to sign up: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1e1e2f 0%, #2d3748 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'Poppins, sans-serif' }}>
      <div style={{ backgroundColor: 'rgba(45, 55, 72, 0.9)', padding: '2.5rem', borderRadius: '12px', boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)', width: '100%', maxWidth: '500px', backdropFilter: 'blur(5px)' }}>

        {/* Header - different for demo vs production */}
        {isDemo ? (
          <>
            <h2 style={{ color: '#e0e0e0', textAlign: 'center', marginBottom: '1rem', fontSize: '28px' }}>ZenBidTracker Demo</h2>
            <p style={{ color: '#4A90E2', textAlign: 'center', marginBottom: '1.5rem', fontSize: '16px' }}>
              🎯 Try the full experience - <strong>No payment required!</strong>
            </p>
          </>
        ) : (
          <h2 style={{ color: '#e0e0e0', textAlign: 'center', marginBottom: '1.5rem', fontSize: '28px' }}>Create Your Account</h2>
        )}

        {error && <p style={{ color: '#ff5555', textAlign: 'center', marginBottom: '1rem' }}>{error}</p>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '0.5rem', fontSize: '14px', fontWeight: '500' }}>Your Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#4a5568',
                color: '#e0e0e0',
                border: '1px solid #4a5568',
                borderRadius: '6px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '0.5rem', fontSize: '14px', fontWeight: '500' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#4a5568',
                color: '#e0e0e0',
                border: '1px solid #4a5568',
                borderRadius: '6px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '0.5rem', fontSize: '14px', fontWeight: '500' }}>Your Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#4a5568',
                color: '#e0e0e0',
                border: '1px solid #4a5568',
                borderRadius: '6px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', color: '#e0e0e0', marginBottom: '0.5rem', fontSize: '14px', fontWeight: '500' }}>Company Name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#4a5568',
                color: '#e0e0e0',
                border: '1px solid #4a5568',
                borderRadius: '6px',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '16px'
              }}
            />
          </div>

          {/* Info box - different for demo vs production */}
          {isDemo ? (
            <div style={{ marginBottom: '1.5rem', padding: '18px', backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 193, 7, 0.3)' }}>
              <p style={{ color: '#FFC107', fontSize: '16px', margin: '0 0 12px 0', fontWeight: '600' }}>
                ⚠️ <strong>Demo Account Notice</strong>
              </p>
              <p style={{ color: '#e0e0e0', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                This is a demo version. Your data will be automatically cleared every 6 hours.
                To keep your data permanently, <strong><a href="https://zenbidtracker.web.app/signup" target="_blank" rel="noopener noreferrer" style={{color: '#e0e0e0', textDecoration: 'underline'}}>upgrade to the full version</a></strong> of ZenBidTracker.
              </p>
            </div>
          ) : (
            <div style={{ marginBottom: '1.5rem', padding: '18px', backgroundColor: 'rgba(74, 144, 226, 0.1)', borderRadius: '8px', border: '1px solid rgba(74, 144, 226, 0.3)' }}>
              <p style={{ color: '#4A90E2', fontSize: '16px', margin: '0 0 12px 0', fontWeight: '600' }}>
                💳 <strong>First License - $50/month</strong>
              </p>
              <p style={{ color: '#e0e0e0', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                You'll be redirected to PayPal to securely pay for your first license. This gives you full access to ZenBidTracker and you can add team members later.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: loading ? '#666' : 'linear-gradient(90deg, #4A90E2 0%, #357ABD 100%)',
              color: '#e0e0e0',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {isDemo
              ? (loading ? 'Creating Demo Account...' : 'Start Demo - Free Access')
              : (loading ? 'Creating Account...' : 'Create Account & Subscribe')
            }
          </button>
        </form>
      </div>
    </div>
  );
}

export default Signup;
